// Counterparty Detail PR 2 — polymorphic Deals tab for the Person, Entity and
// Business detail pages (it also takes a Broker; the Broker detail page uses
// BrokerDealsTab). Mirrors BrokerDealsTab's structure
// (filter chips → DataTable → pagination).
//
// Backend: filters via the personId/entityId/businessId query params on
// GET /api/deals (server maps person/entity ids via party provenance).
// Counterparty sunset: deal rows no longer carry person/entity FKs — only
// borrowerPartyId/sellerPartyId — so the old client-derived Role column
// (Borrower / Seller / Both) is gone.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { Pagination } from "@/components/layout/Pagination";
import { useAuth } from "@/hooks/useAuth";
import { useDealsList } from "@/hooks/deals/useDeals";
import { NewDealWizard } from "@/components/deals/NewDealWizard";
import {
  ageBadgeStatus,
  ageSeverity,
  dealLabel,
  formatCurrency,
  formatStageAge,
} from "@/components/deals/dealFormat";
import {
  ACTIVE_STAGES,
  DEAL_PERMISSIONS,
  STAGE_LABEL,
  subStateLabelFor,
  type DealListItemDto,
  type DealListQuery,
  type DealPipelineStage,
} from "@/types/deals";
import type { BadgeProps } from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";

export type CounterpartyDealsTabProps =
  | { kind: "Person"; personId: string }
  | { kind: "Entity"; entityId: string }
  | { kind: "Business"; businessId: string }
  | { kind: "Broker"; brokerId: string };

type FilterMode = "all" | "active" | "terminated" | "archived";

const PAGE_SIZE = 20;

const FILTER_CHIPS: { key: FilterMode; label: string }[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "terminated", label: "Declined / Withdrawn" },
  { key: "archived", label: "Archived" },
];

const STAGE_BADGE_STATUS: Record<DealPipelineStage, NonNullable<BadgeProps["status"]>> = {
  Intake: "info",
  PreApproval: "gold",
  Underwriting: "gold",
  Closing: "finalized",
  Closed: "draft",
  Declined: "error",
  Withdrawn: "archived",
};

interface KindMeta {
  /** Subject phrasing for empty-state copy ("for this {subject} yet"). */
  subject: string;
}

function kindMetaFor(kind: CounterpartyDealsTabProps["kind"]): KindMeta {
  switch (kind) {
    case "Person":
      return { subject: "person" };
    case "Entity":
      return { subject: "entity" };
    case "Business":
      return { subject: "business" };
    case "Broker":
      return { subject: "broker" };
  }
}

function buildQueryFilter(
  props: CounterpartyDealsTabProps,
): Pick<DealListQuery, "personId" | "entityId" | "businessId" | "brokerId"> {
  switch (props.kind) {
    case "Person":
      return { personId: props.personId };
    case "Entity":
      return { entityId: props.entityId };
    case "Business":
      return { businessId: props.businessId };
    case "Broker":
      return { brokerId: props.brokerId };
  }
}

export function CounterpartyDealsTab(props: CounterpartyDealsTabProps) {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission(DEAL_PERMISSIONS.Create);
  const meta = kindMetaFor(props.kind);
  const { date } = useDateFormatter();

  const [filterMode, setFilterMode] = useState<FilterMode>("active");
  const [page, setPage] = useState(1);
  const [wizardOpen, setWizardOpen] = useState(false);

  const filterKey = JSON.stringify(buildQueryFilter(props));
  const query: DealListQuery = useMemo(() => {
    const base: DealListQuery = {
      ...buildQueryFilter(props),
      page,
      pageSize: PAGE_SIZE,
    };
    switch (filterMode) {
      case "all":
        return { ...base, includeArchived: true, includeClosed: true, status: "all" };
      case "active":
        return { ...base, stage: [...ACTIVE_STAGES], includeArchived: false };
      case "terminated":
        return { ...base, stage: ["Declined", "Withdrawn"], includeArchived: false, status: "all" };
      case "archived":
        return { ...base, archivedOnly: true, includeClosed: true, status: "all" };
    }
    // Re-key on the props subject (e.g. switching counterparty IDs) so
    // the memo stays correct without listing each prop variant directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, filterMode, page]);

  const dealsQuery = useDealsList(query);
  const rows = dealsQuery.data?.data ?? [];
  const totalCount = dealsQuery.data?.totalCount ?? 0;
  const pageCount = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1;

  const columns: Column<DealListItemDto>[] = [
    {
      key: "dealNumber",
      header: "Deal #",
      render: (d) => (
        <Link
          to={`/pipeline/${d.id}`}
          className="font-mono text-caption text-gold-shadow hover:text-gold-highlight"
        >
          {d.dealNumber}
        </Link>
      ),
    },
    {
      key: "businessName",
      header: "Business",
      render: (d) => (
        <Link to={`/pipeline/${d.id}`} className="font-semibold hover:text-gold-shadow">
          {dealLabel(d)}
        </Link>
      ),
    },
    {
      key: "stage",
      header: "Stage",
      render: (d) => (
        <div className="flex flex-col gap-space-1">
          <Badge status={STAGE_BADGE_STATUS[d.stage]}>{STAGE_LABEL[d.stage]}</Badge>
          <span className="text-caption text-gray-mid">{subStateLabelFor(d.stage, d.subState)}</span>
        </div>
      ),
    },
    {
      key: "purchasePrice",
      header: "Price",
      className: "text-right",
      render: (d) => (
        <span className="font-mono tabular-nums">{formatCurrency(d.purchasePrice)}</span>
      ),
    },
    {
      key: "age",
      header: "Days in stage",
      render: (d) => {
        const sev = ageSeverity(d);
        return <Badge status={ageBadgeStatus(sev)}>{formatStageAge(d.stageEnteredDate)}</Badge>;
      },
    },
    {
      key: "createdAt",
      header: "Created",
      render: (d) => (
        <span className="text-caption text-gray-mid">
          {date(d.createdAt)}
        </span>
      ),
    },
  ];

  const showEmptyCta = !dealsQuery.isLoading && rows.length === 0 && filterMode === "active";

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex items-center justify-between gap-space-3 flex-wrap">
        <FilterChips
          value={filterMode}
          onChange={(next) => {
            setFilterMode(next);
            setPage(1);
          }}
        />
        {canCreate && (
          <Button size="sm" onClick={() => setWizardOpen(true)}>
            <Plus size={14} aria-hidden /> New deal
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={dealsQuery.isLoading}
        error={dealsQuery.isError ? `Could not load deals for this ${meta.subject}.` : null}
        rowKey={(d) => d.id}
        emptyMessage={
          showEmptyCta
            ? `No deals for this ${meta.subject} yet.`
            : filterMode === "terminated"
              ? "No declined or withdrawn deals."
              : filterMode === "archived"
                ? "No archived deals."
                : "No deals match the current filter."
        }
      />

      {showEmptyCta && canCreate && (
        <div className="text-center">
          <Button onClick={() => setWizardOpen(true)}>
            <Plus size={14} aria-hidden /> New deal
          </Button>
        </div>
      )}

      {totalCount > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount}
          totalPages={pageCount}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
        />
      )}

      {/* For a Broker subject, prefill that broker (still changeable). The
          borrower/seller/business counterparties are chosen in the wizard. */}
      <NewDealWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialBrokerId={props.kind === "Broker" ? props.brokerId : undefined}
      />
    </div>
  );
}

function FilterChips({
  value,
  onChange,
}: {
  value: FilterMode;
  onChange: (next: FilterMode) => void;
}) {
  return (
    <div role="radiogroup" aria-label="Deal filter" className="inline-flex flex-wrap gap-space-2">
      {FILTER_CHIPS.map((chip) => {
        const active = chip.key === value;
        return (
          <button
            key={chip.key}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(chip.key)}
            className={
              "inline-flex items-center px-space-3 py-[4px] rounded-pill text-caption font-display uppercase tracking-label border transition-colors " +
              (active
                ? "bg-onyx text-gold border-onyx"
                : "bg-white-pure text-gray-mid border-border-light hover:text-onyx hover:border-gold/40")
            }
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}
